import React, { useState, useEffect } from 'react';
import { SvgIcon } from '../SvgIcon';

/**
 * ApplicationIcon Component
 *
 * A reusable component that displays a logo image with automatic fallback to a default icon
 * if the image fails to load or doesn't exist.
 *
 * @param {string} imageUrl - The URL of the logo image to display
 * @param {string} fallbackIcon - The name of the SvgIcon to use as fallback (default: 'app-icon')
 * @param {string} className - CSS class for the image element
 * @param {string} wrapperClass - CSS class for the SvgIcon wrapper
 * @param {string} alt - Alt text for the image
 * @param {object} style - Additional styles for the container
 */
export const ApplicationIcon = ({
  as: Tag = 'div',
  imageUrl,
  fallbackIcon = 'app-icon',
  fallbackComponent = null,
  imgClassName = 'application-icon',
  iconClassName = 'application-icon default',
  wrapperClassName = 'application-icon-wrapper',
  alt = '',
  style = {}
}) => {
  const [imageStatus, setImageStatus] = useState('loading'); // 'loading', 'success', 'error'

  useEffect(() => {
    if (!imageUrl) {
      setImageStatus('error');
      return;
    }

    setImageStatus('loading');

    // Create a new image element to test if the URL is valid
    const img = new Image();

    img.onload = () => {
      setImageStatus('success');
    };

    img.onerror = () => {
      setImageStatus('error');
    };

    img.src = imageUrl;

    // Cleanup function
    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [imageUrl]);

  // Validate that Tag is a valid HTML tag or React component
  if (typeof Tag !== "string" && typeof Tag !== "function") {
    console.error("Invalid tag/component passed to DynamicTag");
    return null;
  }


  // Show loading state briefly, then show result
  if (imageStatus === 'loading') {
    return fallbackComponent || (
      <Tag className={wrapperClassName}>
        <SvgIcon
          name={fallbackIcon}
          wrapperClass={iconClassName}
          style={style}
        />
      </Tag>
    );
  }

  // Show image if it loaded successfully
  if (imageStatus === 'success' && imageUrl) {
    return (
      <Tag className={wrapperClassName}>
        <img
          src={imageUrl}
          alt={alt}
          className={imgClassName}
          style={style}
        />
      </Tag>
    );
  }

  // Show fallback icon if image failed to load or no URL provided
  return fallbackComponent || (
    <Tag className={wrapperClassName}>
      <SvgIcon
        name={fallbackIcon}
        wrapperClass={iconClassName}
        style={style}
      />
    </Tag>
  );
};